/**
 * Utilidades compartidas entre componentes de formularios
 */

import { firstValueFrom, type Observable } from 'rxjs';

// Constantes para validación de archivos de evidencia

export const ALLOWED_EVIDENCE_MIME_TYPES = new Set<string>([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/heif',
  'image/heic',
  'image/webp',
]);

export const ALLOWED_EVIDENCE_EXTENSIONS = new Set<string>([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'heif',
  'heic',
  'webp',
]);

const EVIDENCE_MIME_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  heif: 'image/heif',
  heic: 'image/heic',
  webp: 'image/webp',
};

export const ALLOWED_EVIDENCE_ACCEPT = [
  ...Array.from(ALLOWED_EVIDENCE_MIME_TYPES),
  ...Array.from(ALLOWED_EVIDENCE_EXTENSIONS).map(
    (extension) => `.${extension}`,
  ),
].join(',');

export const ALLOWED_EVIDENCE_FORMATS_LABEL =
  '.pdf, .png, .jpg, .jpeg, .heif, .heic, .webp';

/**
 * Obtiene la clave única de un campo
 * @param field - Objeto del campo
 * @returns La clave del campo (field_name o label)
 */
export function getFieldKey(field: any): string {
  return String(field?.field_name || field?.label || '');
}

/**
 * Formatea una etiqueta de campo para mostrarla en la UI
 * Elimina el sufijo __c, convierte camelCase a espacios, y reemplaza guiones bajos
 * @param rawLabel - Etiqueta cruda del campo
 * @returns Etiqueta formateada
 */
export function formatLabel(rawLabel?: string | null): string {
  const label = String(rawLabel || '');
  const base = label.replace(/__c$/i, '');
  return base
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
}

/**
 * Valida si un archivo está permitido según los tipos MIME y extensiones permitidas
 * @param file - Archivo a validar
 * @returns true si el archivo está permitido, false en caso contrario
 */
export function isAllowedEvidenceFile(file: File): boolean {
  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType) {
    return ALLOWED_EVIDENCE_MIME_TYPES.has(mimeType);
  }

  const fileName = String(file.name || '');
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex >= 0) {
    const extension = fileName.substring(dotIndex + 1).toLowerCase();
    return ALLOWED_EVIDENCE_EXTENSIONS.has(extension);
  }

  return false;
}

export function getEvidenceFileMimeType(file: File): string {
  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType) {
    return mimeType;
  }

  const fileName = String(file.name || '');
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex < 0) {
    return '';
  }

  const extension = fileName.substring(dotIndex + 1).toLowerCase();
  return EVIDENCE_MIME_TYPE_BY_EXTENSION[extension] || '';
}

/**
 * Convierte un archivo a base64 (Data URL).
 */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}

/**
 * Formatea una fecha ISO/string a dd/mm/yyyy en español.
 */
export function formatFecha(fecha: string): string {
  const date = new Date(fecha);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export async function uploadEvidenceWithPresignedUrl(opts: {
  file: File;
  fileName: string;
  getPresignedUrl: (
    nombreArchivo: string,
    tipoArchivo: string,
  ) => Observable<any>;
  uploadFileToPresignedUrl: (
    urlPrefirmada: string,
    file: File,
  ) => Observable<any>;
}): Promise<string> {
  const tipoArchivo = getEvidenceFileMimeType(opts.file);
  if (!tipoArchivo || !ALLOWED_EVIDENCE_MIME_TYPES.has(tipoArchivo)) {
    throw new Error('El tipo de archivo adjunto no está permitido.');
  }

  const presignedResponse = await firstValueFrom(
    opts.getPresignedUrl(opts.fileName, tipoArchivo),
  );
  const urlPrefirmada = presignedResponse?.urlPrefirmada;
  const key = presignedResponse?.key;

  if (!urlPrefirmada || !key) {
    throw new Error('La respuesta del servicio de URL prefirmada es inválida.');
  }

  await firstValueFrom(opts.uploadFileToPresignedUrl(urlPrefirmada, opts.file));
  return String(key);
}

export const ESTADOS_CASOS = {
  'Pendiente de Asignación': {
    etiqueta: 'Pendiente',
    clase: 'pendiente',
  },
  Pendiente: {
    etiqueta: 'Pendiente',
    clase: 'pendiente',
  },
  'En Tratamiento': {
    etiqueta: 'En Curso',
    clase: 'en_curso',
  },
  'Actividad Cliente': {
    etiqueta: 'En Curso',
    clase: 'en_curso',
  },
  Resuelto: {
    etiqueta: 'Resuelto',
    clase: 'resuelto',
  },
};
